// AddQuestionDialog.jsx - Yeni soru ekleme dialog'u
import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { addQuestionToTest } from '../../redux/testSlice';
import QuestionFormFields from './QuestionFormFields';

const AddQuestionDialog = ({ open, onClose, testId, questionNumber, loading, questionNumbers = [] }) => {
  const dispatch = useDispatch();
  
  const [newQuestion, setNewQuestion] = useState({
    questionText: "",
    questionNumber: questionNumber,
    questionType: "",
    options: [],
    minScale: 1,
    maxScale: 5,
    minLabel: "",
    maxLabel: ""
  });
  
  const [currentOptions, setCurrentOptions] = useState([]);
  const [newOption, setNewOption] = useState({ text: "" }); // value kaldırıldı

  const handleQuestionTypeChange = (newType) => {
    setNewQuestion({...newQuestion, questionType: newType});
    setCurrentOptions([]);
    
    if (newType === "YES_NO") {
      setCurrentOptions([
        { text: "Yes", value: 1 },
        { text: "No", value: 0 }
      ]);
    }
  };

  const resetForm = useCallback(() => {
    setNewQuestion({
      questionText: "",
      questionNumber: questionNumber,
      questionType: "",
      options: [],
      minScale: 1,
      maxScale: 5,
      minLabel: "",
      maxLabel: ""
    });
    setCurrentOptions([]);
    setNewOption({ text: "" }); // value kaldırıldı
  }, [questionNumber]);

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  const handleSubmit = () => {
    if (!newQuestion.questionText || !newQuestion.questionType) {
      toast.error("Please enter question text and type!");
      return;
    }
    
    const normalizedQuestionNumber = Number(newQuestion.questionNumber);

    if (!Number.isInteger(normalizedQuestionNumber) || normalizedQuestionNumber < 1) {
      toast.error("Question number must be a positive whole number!");
      return;
    }

    if (questionNumbers.includes(normalizedQuestionNumber)) {
      toast.error("Question number must be unique. Another question already uses that number.");
      return;
    }

    if (newQuestion.questionType === "SCALE") {
      if (!newQuestion.minLabel?.trim() || !newQuestion.maxLabel?.trim()) {
        toast.error("Scale questions need both minimum and maximum labels!");
        return;
      }
    }
    
    let questionData = { ...newQuestion, questionNumber: normalizedQuestionNumber };

    if (newQuestion.questionType === "MULTIPLE_CHOICE" || newQuestion.questionType === "YES_NO") {
      if (currentOptions.length < 2) {
        toast.error("You must add at least 2 options!");
        return;
      }
      questionData.options = currentOptions;
    }

    if (newQuestion.questionType === "EMERGENCY_CONTACT") {
      // Backend doesn't know EMERGENCY_CONTACT type — store as TEXT
      // with a marker prefix so the frontend can detect it later
      questionData.questionType = "TEXT";
      questionData.questionText = `__EMERGENCY_CONTACT__\n${newQuestion.questionText}`;
      questionData.options = [];
    }

    dispatch(addQuestionToTest({
      testId: testId,
      questionData
    }));
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Add New Question</DialogTitle>
      <DialogContent>
        <QuestionFormFields
          question={newQuestion}
          setQuestion={setNewQuestion}
          currentOptions={currentOptions}
          setCurrentOptions={setCurrentOptions}
          newOption={newOption}
          setNewOption={setNewOption}
          onQuestionTypeChange={handleQuestionTypeChange}
          isEdit={false}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={resetForm} variant="outlined">
          Clear
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddQuestionDialog;
